import React, {memo, useMemo, useState} from 'react'
import {
  Handle,
  Position,
  type NodeProps,
  useNodes,
  type Node,
  useReactFlow,
  type Edge,
} from 'reactflow'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import {TIMING} from '../../../../../../Utils/Constant/FlowConstants'
import {type DataNode} from '../../FlowTypes'
import {useFlowNodeDelete} from '../hooks/useFlowNodeDelete'

export interface WaitForReplyNodeProps extends NodeProps {}

type DeletableDataNode = DataNode<unknown> & {deletable?: boolean}

const WaitForReplyNode: React.FC<WaitForReplyNodeProps> = ({id, selected}) => {
  const {t} = useI18n()
  const {setNodes, setEdges, getEdges} = useReactFlow()
  const nodes = useNodes()
  const edges = getEdges()
  const [hovered, setHovered] = useState(false)

  const item = useMemo(
    () => TIMING.find((timingItem) => timingItem.type === 'Wait_for_reply') ?? TIMING[0],
    []
  )

  const node = useMemo(
    () => nodes.find((n) => n.id === id) as Node<DeletableDataNode> | undefined,
    [nodes, id]
  )

  const {handleDelete} = useFlowNodeDelete({
    nodes,
    edges,
    setNodes,
    setEdges,
    pushToUndoStack: undefined,
    isUndoRedoOperation: undefined,
  })

  const title = t('FLOW.NODE.WAIT_FOR_REPLY_DESC')

  return (
    <div
      className={`p-3 rounded bg-white text-center position-relative shadow-sm${
        selected ? ' border border-2 border-primary' : ''
      } nopan${hovered ? ' node-hover' : ''}`}
      style={{width: 250, minHeight: 90}}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={title}
    >
      {node?.data?.deletable !== false && (
        <button
          className='btn btn-link p-0 position-absolute end-0 top-0 m-2'
          style={{zIndex: 10}}
          title='Delete Node'
          onClick={(e) => {
            if (node) {
              handleDelete(id, node, e)
            }
          }}
        >
          <i className='bi bi-trash text-danger fs-5'></i>
        </button>
      )}

      <Handle
        type='target'
        position={Position.Top}
        id='waitforreply-top'
        style={{background: 'var(--bs-gray-600)'}}
      />

      <div className='d-flex flex-column gap-2'>
        <div className='d-flex align-items-center gap-2'>
          <div
            className='automation-icon-container'
            style={{
              backgroundColor: item.bgColor,
            }}
          >
            <img
              src={toAbsoluteUrl(item.svg || '')}
              alt={item.icon}
              className='automation-icon'
            />
          </div>

          <span
            className='text-truncate fw-medium'
            style={{maxWidth: '140px'}}
            title={title}
          >
            {t(`FLOW.NODE.${item.type.toUpperCase()}`)}
          </span>
        </div>

        <p
          className='mb-0 small text-truncate'
          style={{
            maxWidth: 220,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={title}
        >
          {t('FLOW.NODE.WAIT_FOR_REPLY_SUMMARY')}
        </p>
      </div>

      <Handle
        type='source'
        position={Position.Bottom}
        id='waitforreply-bottom'
        className='bg-primary'
      />
    </div>
  )
}

export default memo(WaitForReplyNode)

